<template>
  <div
    v-if="!isLoading2"
    class="section"
  >
    <div class="columns is-centered">
      <div class="column is-11">
        <div
          v-if="changedProfileFields.length > 0"
          class="section"
          :class="positionMatched !== userPositions.length ? 'divider' : ''"
        >
          <div class="columns is-multiline">
            <div class="column is-12 has-text-centered">
              <h2 class="title is-2">
                Update User Profile
              </h2>
              <div class="subtitle is-2">
                The information you have entered in the previous filing screen is different from the information stored in your user profile. Please review the discrepancies and choose to update your profile with the new information as desired.
              </div>
            </div>
            <div class="column is-12">
              <table class="table is-bordered is-striped is-fullwidth table-content-center">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Current Filing Contains</th>
                    <th>User Profile Contains</th>
                    <th class="has-text-centered">
                      Update
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="field in changedProfileFields"
                    :key="field.name"
                  >
                    <td>{{ field.label }}</td>
                    <td>{{ field.currentFiling }}</td>
                    <td>{{ field.userProfile }}</td>
                    <td class="has-text-centered">
                      <checkbox-adv
                        :id="field.name"
                        v-model="fieldsToUpdate"
                        class="has-no-label"
                        :value="field.name"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div
          v-if="positionMatched !== userPositions.length"
          class="section"
        >
          <div class="columns is-multiline">
            <div class="column is-12 has-text-centered">
              <h2 class="title is-2">
                Update Profile Positions
              </h2>
              <div class="subtitle is-2">
                Some positions in the current filing have been updated.
              </div>
            </div>
            <div class="column is-6">
              <div class="is-size-4">
                Current Filing Positions
              </div>
              <table
                v-for="position in currentFilingPositions"
                :key="'cf-' + position.id"
                class="table is-bordered is-striped is-fullwidth table-content-center"
              >
                <tbody>
                  <tr>
                    <td colspan="2">
                      <b>{{ positionFieldsLabels['publicPosition'] }}: </b> {{ position.publicPosition }}
                    </td>
                    <td>
                      <b>{{ positionFieldsLabels['profession'] }}: </b> {{ position.profession }}
                    </td>
                  </tr>
                  <tr>
                    <td colspan="2">
                      <b>{{ positionFieldsLabels['agencyId'] }}: </b> {{ getControlValueDesc(position.agencyId, 'agencies') }}
                    </td>
                    <td>
                      <b>{{ positionFieldsLabels['affiliationStatus'] }}: </b> {{ getControlValueDesc(position.affiliationStatus, 'affiliationStatuses') }}
                    </td>
                  </tr>
                  <tr>
                    <td colspan="3">
                      <b>{{ positionFieldsLabels['workStreetAddress'] }}:</b> {{ position.workStreetAddress }}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>{{ positionFieldsLabels['workCity'] }}:</b> {{ position.workCity }}
                    </td>
                    <td>
                      <b>{{ positionFieldsLabels['workState'] }}:</b> {{ position.workState ? getControlValueDesc(position.workState, 'states') : '' }}
                    </td>
                    <td>
                      <b>{{ positionFieldsLabels['workZip'] }}:</b> {{ position.workZip }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="column is-6">
              <div class="is-size-4">
                User Profile Positions
              </div>
              <table
                v-for="position in userPositions"
                :key="'up-' + position.id"
                class="table is-bordered is-striped is-fullwidth table-content-center"
              >
                <tbody>
                  <tr>
                    <td colspan="2">
                      <b>{{ positionFieldsLabels['publicPosition'] }}: </b> {{ position.publicPosition }}
                    </td>
                    <td>
                      <b>{{ positionFieldsLabels['profession'] }}: </b> {{ position.profession }}
                    </td>
                  </tr>
                  <tr>
                    <td colspan="2">
                      <b>{{ positionFieldsLabels['agencyId'] }}: </b> {{ getControlValueDesc(position.agencyId, 'agencies') }}
                    </td>
                    <td>
                      <b>{{ positionFieldsLabels['affiliationStatus'] }}: </b> {{ getControlValueDesc(position.affiliationStatus, 'affiliationStatuses') }}
                    </td>
                  </tr>
                  <tr>
                    <td colspan="3">
                      <b>{{ positionFieldsLabels['workStreetAddress'] }}:</b> {{ position.workStreetAddress }}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>{{ positionFieldsLabels['workCity'] }}: </b>{{ position.workCity }}
                    </td>
                    <td>
                      <b>{{ positionFieldsLabels['workState'] }}: </b>{{ position.workState ? getControlValueDesc(position.workState, 'states') : '' }}
                    </td>
                    <td>
                      <b>{{ positionFieldsLabels['workZip'] }}: </b>{{ position.workZip }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="column is-12">
              <checkbox-adv
                id="overridePositions"
                v-model="overridePositions"
                :value="true"
              >
                Check this box to override the positions stored in the <b>user profile</b> (right side) with the positions in the <b>current filing</b> (left side).
              </checkbox-adv>
            </div>
          </div>
        </div>
        <div class="columns is-centered">
          <div class="column is-12 has-text-centered">
            <button
              id="save-btn"
              type="button"
              class="button is-primary"
              :class="isSaving ? 'is-loading' : ''"
              :disabled="isSaving"
              @click="saveAndContinue"
            >
              Save and Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import Vue from 'vue';
import { ClientTable } from 'vue-tables-2';
import { filerScreenHelpers } from '@/mixins/filerScreen';
import { controlValueHelpers } from '@/mixins/controlValue';
import CheckboxAdv from '@/components/phila-ui/CheckboxAdv';

Vue.use(ClientTable);

export default {
  name: 'User',
  components: {
    CheckboxAdv,
  },
  mixins: [
    filerScreenHelpers,
    controlValueHelpers,
  ],
  data() {
    return {
      highlights: [],
      currentFiling: null,
      currentFilingPositions: [],
      fieldsToUpdate: [],
      isSaving: false,
      userPositions: [],
      userProfile: null,
      positionFieldsLabels: {
        publicPosition: 'Position',
        agencyId: 'Agency',
        affiliationStatus: 'Affiliation Status',
        profession: 'Profession',
        workStreetAddress: 'Address',
        workCity: 'City',
        workState: 'State',
        workZip: 'Zip Code',
        workPhone: 'Phone',
      },
      overridePositions: false,
    };
  },
  computed: {
    affiliationStatuses () {
      return this.$store.getters[ 'admin/activeControlValues' ]('affiliationstatus');
    },
    agencies () {
      return this.$store.getters[ 'admin/activeControlValues' ]('agency');
    },
    positionMatched() {

      let positionsMatched = 0;

      if (this.userPositions && this.currentFilingPositions) {
        let fields = [
          'publicPosition',
          'agencyId',
          'affiliationStatus',
          'profession',
          'workStreetAddress',
          'workCity',
          'workState',
          'workZip',
          'workPhone',
        ];

        let matchCount = fields.length;
        this.currentFilingPositions.forEach(filingPosition => {
          this.userPositions.forEach(userPosition => {
            let currentFieldMatchCount = 0;
            fields.forEach(field => {
              if (filingPosition[field] === userPosition[field]) {
                currentFieldMatchCount++;
              }
            });
            if (currentFieldMatchCount === matchCount) {
              positionsMatched++;
            }
          }, this);
        }, this);
      }
      return positionsMatched;
    },
    changedProfileFields() {
      let changed = [];

      if (this.userProfile && this.currentFiling) {
        let fields = [
          'firstName',
          'midInit',
          'lastName',
          'suffix',
          'homeStreetAddress',
          'homeCity',
          'homeState',
          'homeZip',
          'homePhone',
          'countyOfResidence',
        ];
        let fieldsLabels = [
          'First Name',
          'Middle Initial',
          'Last Name',
          'Suffix',
          'Street Address',
          'City',
          'State',
          'Zip Code',
          'Home Phone',
          'County of Residence',
        ];

        fields.forEach((field, index) => {
          if (this.currentFiling[field] !== this.userProfile[field]) {
            let currentFiling = this.currentFiling[field];
            let userProfile = this.userProfile[field];

            if (field === 'homeState') {
              currentFiling = this.getControlValueDesc(currentFiling, 'states');
              userProfile = this.getControlValueDesc(userProfile, 'states');
            }
            if (field === 'countyOfResidence') {
              currentFiling = this.getControlValueDesc(currentFiling, 'paCounties');
              userProfile = this.getControlValueDesc(userProfile, 'paCounties');
            }

            changed.push({
              name: field,
              label: fieldsLabels[index],
              currentFiling,
              userProfile,
            });

          }
        });
      }
      return changed;
    },
    paCounties () {
      return this.$store.getters[ 'admin/activeControlValues' ]('county');
    },
    states () {
      return this.$store.getters[ 'admin/activeControlValues' ]('state');
    },
  },
  async created () {
    await this.initialFetch();
    await this.initialAssign();
    if (!this.profileHasUpdates()) {
      this.$router.push({ name: this.nextFilingScreen });
    }
    this.$store.commit('loading', false);
  },
  methods: {
    async initialAssign() {
      this.userProfile = this.getUserProfile();
      this.userPositions = this.getUserPositions();
      this.currentFiling = this.getCurrentFiling();
      this.currentFilingPositions = this.getCurrentFilingPositions();
    },
    async initialFetch () {
      let promises = [];
      promises.push(this.$store.dispatch('admin/getControlValue', 'county'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'agency'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'state'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'affiliationstatus'));
      promises.push(this.$store.dispatch('user/fetchUserPositions'));
      promises.push(this.$store.dispatch('filing/fetchCurrentFilingPositions'));
      promises.push(this.$store.dispatch('filing/fetchCurrentFilingFilerStatus'));
      return Promise.all(promises);
    },
    getUserProfile () {
      return this.cloneObj(this.$store.state.user.profile);
    },
    getUserPositions () {
      return this.cloneObj(this.$store.state.user.positions);
    },
    getCurrentFiling () {
      if (this.$store.state.filing.filing) {
        return this.cloneObj(this.$store.state.filing.filing);
      }
      return [];
    },
    getCurrentFilingPositions () {
      if (this.$store.state.filing.currentFilingPositions) {
        return this.cloneObj(this.$store.state.filing.currentFilingPositions);
      }
      return [];
    },
    profileHasUpdates () {
      if (this.changedProfileFields.length === 0 && (this.positionMatched === this.userPositions.length)){
        return false;
      }
      return true;
    },
    async saveAndContinue () {
      let fields = {};
      this.fieldsToUpdate.forEach(field => {
        fields[field] = this.currentFiling[field];
      }, this);

      if (Object.keys(fields).length > 0) {
        await this.$store.dispatch('user/updateUserProfile', {
          fields,
          userId: this.$store.state.login.loggedIn.id,
        });
      }

      if (this.overridePositions) {
        let positions = this.cloneObj(this.userPositions);
        positions.forEach(position => {
          if (!position.cud) {
            position.cud = 'delete';
          }
        });


        let filingPositions = this.cloneObj(this.currentFilingPositions);
        filingPositions.forEach(position => {
          position.cud = 'post';
          positions.push(position);
        });

        await this.$store.dispatch('user/saveUserPositions', { positions, userId: this.$store.state.login.loggedIn.id }).then(() => {

        });

      }

      this.$router.push({ name: this.nextFilingScreen });

    },
  },
};
</script>

<style scoped lang="scss">
  .positions-comparison {
    .left-positions, .right-positions {
      vertical-align: top;
      width:50%;
      max-width: 50%;
    }
  }
</style>
